import { RcIcon } from '@/components/atoms/RcIcon'
import { RcTrans } from '@/components/atoms/RcTrans'
import { RcAvatar } from '@/components/molecules/text/RcAvatar'
import { NavigationLink } from '@/components/organisms/navigation/NavigationLink'
import { Trophy } from '@/components/organisms/site/Trophy'
import { ExpansionType, expand } from '@/core/expand'
import { useNavigation } from '@/core/hooks/useNavigation'
import { RootRoute } from '@/core/route-keys'
import { useCompetitionTeam } from '@/entity/competition-team/useCompetitionTeam'
import { useCompetition } from '@/entity/competition/useCompetition'
import { useCompetitionActions } from '@/entity/competition/useCompetitionActions'
import { useCompetitionEvents } from '@/entity/competition/useCompetitionEvents'
import { useCompetitionParticipant } from '@/entity/competition/useCompetitionParticipant'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { EventKind } from '@rallycry/api-suite-typescript/dist/models'
import { CommunityKind } from '@rallycry/api-suite-typescript/dist/models/CommunityKind'
import { find, isEmpty, last } from 'lodash-es'
import { isActiveCheckinIssue } from '../alerts/useCompetitionAlertsCheckin'
import { TeamActions } from './TeamActions'

export const TeamHeader = ({ condensed }: { condensed?: boolean }) => {
  const { isCompetitionModerator } = useCompetition()
  const { involvement } = useCompetitionParticipant()
  const {
    team,
    read: { isLoading, mutate }
  } = useCompetitionTeam()
  const { events } = useCompetitionEvents()
  const { checkin } = useCompetitionActions()
  const { getPath } = useNavigation()

  const representingCommunity = expand(
    team,
    team?._expanded,
    ExpansionType.CompetitionCommunity
  )

  const isTeamAdmin = team?._links?.update
  const participant = find(involvement, it => it.entry?.id === team?.id)

  const communityPath = getPath({
    root:
      representingCommunity?.kind === CommunityKind.BASIC
        ? RootRoute.Community
        : RootRoute.Squad,
    rootId: representingCommunity?.id
  })

  const currentOrPreviousCheckins =
    events([EventKind.CHECK_IN]).allCurrentOrPrevious?.map(it => it.id!) || []

  const mostRecentCheckin = last(currentOrPreviousCheckins)

  const canCheckin =
    isCompetitionModerator ||
    (mostRecentCheckin && (participant || isTeamAdmin))

  const handleCheckin = async () => {
    if (!mostRecentCheckin) return
    await checkin(mostRecentCheckin, team?.id!, false)
    await mutate()
  }

  const CheckInChip = () => {
    return (
      <Box>
        {team?.dropped ? (
          <Chip
            color='warning'
            label={<RcTrans i18nKey='competition:team.dropped' />}
          />
        ) : isLoading || !team ? (
          <Skeleton variant='rounded' animation={false} />
        ) : isEmpty(currentOrPreviousCheckins) ? null : team?._issues?.find(
            it => isActiveCheckinIssue(it, mostRecentCheckin)
          ) ? (
          <Chip
            label={<RcTrans i18nKey='competition:team.checkin-required' />}
            color='error'
            onClick={canCheckin ? handleCheckin : undefined}
            sx={{
              cursor: canCheckin ? 'pointer' : 'inherit',
              borderRadius: 2
            }}
            icon={<RcIcon icon={['fal', 'times']} ml={1} mr={1} />}
          />
        ) : (
          <Chip
            label={<RcTrans i18nKey='competition:team.checkin-complete' />}
            color='success'
            sx={{ borderRadius: 2 }}
            icon={<RcIcon icon={['fal', 'check']} ml={1} mr={1} />}
          />
        )}
      </Box>
    )
  }

  return (
    <Stack
      direction={condensed ? 'column' : 'row'}
      spacing={condensed ? 2 : 4}
      justifyContent={'space-between'}
      alignItems={condensed ? 'flex-start' : 'center'}
      py={4}
      px={4}
    >
      <Stack direction='row'>
        {representingCommunity ? (
          <NavigationLink to={communityPath}>
            <RcAvatar
              src={team?.image}
              sx={{ width: 60, height: 60, mr: 2 }}
              variant='rounded'
            />
          </NavigationLink>
        ) : (
          <RcAvatar
            src={team?.image}
            sx={{ width: 60, height: 60, mr: 2 }}
            variant='rounded'
          />
        )}

        <Stack direction='column' spacing={1}>
          <Stack
            direction='row'
            spacing={3}
            flexGrow={1}
            pr={2}
            alignItems={'center'}
          >
            <Typography variant='h2' color='text.primary'>
              {team?.alternateName}
              {team?.abbreviation ? ` (${team?.abbreviation})` : null}
            </Typography>
            {team?.rank ? <Trophy placement={team?.rank} size='lg' /> : null}
          </Stack>
          {condensed ? null : <CheckInChip />}
        </Stack>
      </Stack>

      <Stack
        direction='row'
        spacing={condensed ? 1 : 2}
        justifyContent={condensed ? 'flex-start' : 'flex-end'}
        alignItems='center'
        sx={{ width: condensed ? '100%' : undefined }}
      >
        {condensed ? (
          <Box flexGrow={1}>
            <CheckInChip />
          </Box>
        ) : null}

        <TeamActions condensed={!!condensed} />
      </Stack>
    </Stack>
  )
}
